import { useEffect, useMemo, useState } from "react"
import { Lead } from "../../../models/Lead"
import FilteredTable from "../../FilteredTable"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "../../../state/store"
import { shortDateFormat } from "../../../utils/functions"
import Button from "../../Button"
import { PauseIcon } from "@heroicons/react/24/solid"
import { LoadingMask } from "../../LoadingMask"
import { toast, ToastContainer } from "react-toastify"
import { DeactivateClientAutomationModal } from "../../clients/automation/DeactivateClientAutomationModal"
import { ClientLeadFrequencyAutomation } from "../../../models/FollowUp"
import { deleteFollowUpMessageExcecution, fetchLeadAutomations } from "../../../state/automations/action"
import { Toast } from "../../Toast"

interface Props {
    lead: Lead
    canEditLead: boolean
}

export const LeadAutomationsModule = (props: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    
    const [isLoadingAutomations, setIsLoadingAutomations] = useState(false);
    const [isDeactivatingAutomation, setIsDeactivatingAutomation] = useState(false);
    const [showAutomationError, setShowAutomationError] = useState(false);
    const leadAutomations = useSelector((state: RootState) => state.automations.leadAutomations);
    const errorLeadAutomation = useSelector((state: any) => state.automations.errorClientLeadAutomations);

    const [ showDeativateClientAutomationModal, setShowDeativateClientAutomationModal] = useState<{
        automation?: any;
        show: boolean;
    }>({ show: false, automation: undefined });

    useEffect(() => {
        if (showAutomationError) {
          Toast(t(errorLeadAutomation?.reason ?? "SOMETHING_WENT_WRONG"), t(errorLeadAutomation?.cause_info));
          setShowAutomationError(false);
        }
    }, [showAutomationError, errorLeadAutomation, t]);

    useEffect(() => {
        //TODO: Review state and get data
        if(props.lead) {
            fecthLeadAutomation()
        }
    }, [props.lead]);

    const fecthLeadAutomation = () => {
        setIsLoadingAutomations(true);
        dispatch(fetchLeadAutomations(props.lead.id!)).then((e) => {
          let automations = (e.payload || []) as ClientLeadFrequencyAutomation[];
          setIsLoadingAutomations(false)
        });
    }

    const deactivateAutomation = (automation: ClientLeadFrequencyAutomation) => {
        //TODO: remove address from database
        setIsDeactivatingAutomation(true);
        dispatch(deleteFollowUpMessageExcecution({ followUpXClientLeadId: automation.id, isClient: false })).then((e) => {
            if (e.type === "followUp/deleteFollowUpMessageExcecution/rejected") {
                setShowAutomationError(true);
                setIsDeactivatingAutomation(false);
            } else {
                toast(t("SUCCESSFULLY_DEACTIVATE_FOLLOW_UP"));
                setIsDeactivatingAutomation(false);
            }
        });
    }

    const automationColumns = useMemo(() => {
        const showColumns = [
            {
                Header: t("DESCRIPTION"),
                Cell: (row: any) => row.row.original.followup_message.description,
            },
            {
                Header: t('FREQUENCY'),
                Cell: (row: any) => row.row.original.followup_message.frequency,
            },
            {
                Header: t('FREQUENCY_UNIT'),
                Cell: ( row: any ) => {
                    const { frequency_unit } = row.row.original.followup_message;
                    return frequency_unit === "day" ? t("DAY") : t("MONTH");
                },
            },
            {
                Header: t('FREQUENCY_TIME'),
                Cell: (row: any) => row.row.original.followup_message.frequency_time,
            },
            {
                Header: t("NEXT_EXECUTION"),
                Cell: (row: any) => row.row.original.next_execution ? `${shortDateFormat(row.row.original.next_execution)}` : "-",
            }
        ]
    
        const actions = {
            id: "actions",
            Header: "",
            Cell: (row: any) => {
                return (
                    <div className="flex flex-row justify-center divide-x">
                        <Button
                            isTerciary
                            iconOnly
                            tooltip={t("DEACTIVATE_AUTOMATION")}
                            onClick={() => setShowDeativateClientAutomationModal({ show: true, automation: row.row.original })}
                            classNames="mr-2"
                        >
                            <PauseIcon className="h-5 aspect-square text-red-600" />
                        </Button>
                    </div>
                );
            },
        }
    
        if(!props.canEditLead) {
            return [...showColumns]
        }
        
        return [...showColumns, actions]
    }, [deactivateAutomation, t]);

    return (
        <div className="text-black max-h-full p-5 w-screen md:w-full relative overflow-x-auto bg-white rounded-lg shadow transition-all duration-500 ease-in-out overflow-hidden">
            <div className="text-xl font-bold text-black pl-3">{t("AUTOMATIONS")}</div>
            {
                isLoadingAutomations &&
                <div className="flex items-center justify-center pt-6 pb-5">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-gold"></div>
                </div>
            }
            {
                !isLoadingAutomations && 
                <div className="pb-5">
                    <FilteredTable
                        columns={automationColumns}
                        data={leadAutomations}
                        hasSearchBar={false}
                    />
                </div>
            }
            {isDeactivatingAutomation && <LoadingMask />}
            <ToastContainer progressStyle={{ background: "#D4AF37" }} />
            <DeactivateClientAutomationModal
                info={showDeativateClientAutomationModal}
                onClose={() => setShowDeativateClientAutomationModal({show : false, automation: undefined})}
                deactivateAutomation={(automation: ClientLeadFrequencyAutomation) => deactivateAutomation(automation)}
            />
        </div>
    )
}